import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, Plus, Pencil, X, Power, PowerOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MasterTable from "@/components/MasterTable";

const EMPTY = {
  name: "", department: "", contact_name: "", contact_phone: "",
  contact_email: "", location: "", address: "", active: true, notes: "",
  company_id: "", department_id: "", cc_number: "",
};

const REQUIRED = ["name", "department", "contact_name", "contact_phone", "location", "address"];

export default function CostCentersSection() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const [cc, co, dep] = await Promise.all([
        api.get("/cost-centers"),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/departments").catch(() => ({ data: [] })),
      ]);
      setItems(cc.data); setCompanies(co.data || []); setDepartments(dep.data || []);
    } catch (_e) { /* silent — feature may not be enabled for tenant */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const openCreate = () => { reset(); setOpen(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    for (const k of REQUIRED) {
      if (!(form[k] || "").trim()) {
        toast.error(`${k.replaceAll("_", " ")} is required`); return;
      }
    }
    try {
      if (editId) await api.patch(`/cost-centers/${editId}`, form);
      else await api.post("/cost-centers", form);
      toast.success(editId ? "Cost center updated" : "Cost center added");
      setOpen(false); reset(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/cost-centers/${r.id}/active`, null, { params: { active: !r.active } });
      toast.success(`${r.name} is now ${!r.active ? "Active" : "Inactive"}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="cost-centers-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2"><Wallet size={12} /> Master data</div>
          <h2 className="font-display text-xl mt-1">Cost Centers</h2>
          <p className="text-sm text-muted-foreground mt-1">Billable departments for outward shipments — required on every outward parcel.</p>
        </div>
        <Button data-testid="cc-new" onClick={openCreate} className="rounded-md bg-primary text-primary-foreground">
          <Plus size={13} className="mr-1" /> Add cost center
        </Button>
      </div>

      <MasterTable
        columns={[
          { key: "name", label: "Cost Center", render: (r) => <span className="font-semibold">{r.name}</span> },
          { key: "department", label: "Billing Dept.", className: "text-xs" },
          { key: "contact_name", label: "Contact", render: (r) => (
              <div className="text-xs">
                <div>{r.contact_name}</div>
                <div className="mono text-muted-foreground">{r.contact_phone}{r.contact_email ? ` · ${r.contact_email}` : ""}</div>
              </div>
          )},
          { key: "location", label: "Location", className: "text-xs" },
          { key: "active", label: "Status", render: (r) => (
              <span
                data-testid={`cc-badge-${r.id}`}
                className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${
                  r.active
                    ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                    : "border-slate-400 text-slate-500 bg-slate-50"
                }`}
              >
                {r.active ? "Active" : "Inactive"}
              </span>
          )},
          { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
              <div className="inline-flex items-center gap-3">
                <button data-testid={`cc-toggle-${r.id}`} onClick={() => toggleActive(r)}
                        className="text-muted-foreground hover:text-primary"
                        title={r.active ? "Deactivate" : "Activate"}>
                  {r.active ? <PowerOff size={13} /> : <Power size={13} />}
                </button>
                <button data-testid={`cc-edit-${r.id}`} onClick={() => openEdit(r)}
                        className="text-muted-foreground hover:text-primary" title="Edit">
                  <Pencil size={13} />
                </button>
              </div>
          )},
        ]}
        rows={items}
        searchKeys={["name", "department", "contact_name", "contact_phone", "contact_email", "location"]}
        getRowKey={(r) => r.id}
        testidPrefix="cc"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No cost centers yet. Add one to enable outward parcel entry."
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="rounded-md max-w-lg" data-testid="cc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit cost center" : "Add cost center"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline">Company *</span>
                <select data-testid="cc-company" value={form.company_id || ""} required
                        onChange={(e) => setForm({ ...form, company_id: e.target.value, department_id: "" })}
                        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">— pick company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline">Department (master) *</span>
                <select data-testid="cc-department-id" value={form.department_id || ""} required
                        onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                        disabled={!form.company_id}
                        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm disabled:bg-muted">
                  <option value="">— pick department —</option>
                  {departments.filter(d => d.company_id === form.company_id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cost Center Name *" testid="cc-name" v={form.name} set={(v) => setForm({ ...form, name: v })} />
              <Field label="CC Number (optional)" testid="cc-number" mono v={form.cc_number} set={(v) => setForm({ ...form, cc_number: v })} />
              <Field label="Billing Department (legacy) *" testid="cc-department" v={form.department} set={(v) => setForm({ ...form, department: v })} />
              <Field label="Contact Person *" testid="cc-contact-name" v={form.contact_name} set={(v) => setForm({ ...form, contact_name: v })} />
              <Field label="Phone Number *" testid="cc-contact-phone" mono v={form.contact_phone} set={(v) => setForm({ ...form, contact_phone: v })} />
              <Field label="Email ID" type="email" testid="cc-contact-email" v={form.contact_email} set={(v) => setForm({ ...form, contact_email: v })} />
              <Field label="Location *" testid="cc-location" v={form.location} set={(v) => setForm({ ...form, location: v })} />
            </div>
            <Field label="Address *" textarea testid="cc-address" v={form.address} set={(v) => setForm({ ...form, address: v })} />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                     className="h-4 w-4 accent-[#BE1E1E]" data-testid="cc-active" />
              <span>Active</span>
              <span className="text-[11px] text-muted-foreground">— inactive centers are hidden from the outward form</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md">
                <X size={13} className="mr-1" />Cancel
              </Button>
              <Button type="submit" data-testid="cc-submit" className="rounded-md bg-primary text-primary-foreground">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Field({ label, v, set, type = "text", testid, mono, textarea }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      {textarea ? (
        <textarea data-testid={testid} value={v || ""} onChange={(e) => set(e.target.value)} rows={2}
                  className="mt-1 w-full rounded-md border border-input p-2 text-sm bg-background" />
      ) : (
        <Input data-testid={testid} type={type} value={v || ""} onChange={(e) => set(e.target.value)}
               className={`mt-1 rounded-md ${mono ? "font-mono" : ""}`} />
      )}
    </label>
  );
}
